import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ADMIN_PALETTE, STATUS_PALETTE } from '../adminDesignSystem';

function ReasonChip({ testId, active, title, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      style={{
        textAlign: 'left', padding: '12px 14px', borderRadius: 14,
        border: `1.5px solid ${active ? STATUS_PALETTE.urgent.solid : ADMIN_PALETTE.border}`,
        background: active ? `${STATUS_PALETTE.urgent.solid}12` : '#FFFFFF',
        color: ADMIN_PALETTE.ink, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 3,
        transition: 'background 140ms ease, border-color 140ms ease',
      }}
    >
      <span style={{ fontSize: 13.5, fontWeight: 800 }}>{title}</span>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: ADMIN_PALETTE.inkMuted }}>{hint}</span>
    </button>
  );
}

export function DeclineReasonModal({ onCancel, onConfirm, saving }) {
  const [reason, setReason] = useState('');
  const canConfirm = !!reason && !saving;
  return (
    <div
      data-testid="admin-experience-decline-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9600,
        background: 'rgba(15,17,21,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', borderRadius: 20, padding: 20,
          width: '100%', maxWidth: 420,
          boxShadow: '0 24px 60px rgba(15,17,21,0.32)',
          fontFamily: "'Manrope', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: ADMIN_PALETTE.ink }}>
          Why are you declining this?
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: ADMIN_PALETTE.inkMuted, lineHeight: 1.4 }}>
          The host will get a notification with your reason so they can fix it and resubmit.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <ReasonChip
            testId="admin-experience-decline-reason-photo"
            active={reason === 'photo'}
            title="The photo isn't good enough"
            hint="Blurry, too dark, not scenic, or off-topic."
            onClick={() => setReason('photo')}
          />
          <ReasonChip
            testId="admin-experience-decline-reason-description"
            active={reason === 'description'}
            title="The description isn't good enough"
            hint="Too short, unclear, or doesn't tell a story."
            onClick={() => setReason('description')}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="admin-experience-decline-cancel"
            style={{
              padding: '11px 14px', borderRadius: 12,
              border: `1.5px solid ${ADMIN_PALETTE.border}`,
              background: '#FFFFFF', color: ADMIN_PALETTE.inkSoft,
              fontSize: 13, fontWeight: 800, cursor: 'pointer', minHeight: 44,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={!canConfirm}
            data-testid="admin-experience-decline-confirm"
            style={{
              padding: '11px 14px', borderRadius: 12,
              border: `1.5px solid ${STATUS_PALETTE.urgent.solid}`,
              background: STATUS_PALETTE.urgent.solid, color: '#FFFFFF',
              fontSize: 13, fontWeight: 800,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.5, minHeight: 44,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Send & decline
          </button>
        </div>
      </div>
    </div>
  );
}

export function ToggleChip({ testId, active, onClick, tone, label, count }) {
  const palette = STATUS_PALETTE[tone] || STATUS_PALETTE.neutral;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 14px', borderRadius: 9999,
        border: `1.5px solid ${active ? palette.solid : ADMIN_PALETTE.border}`,
        background: active ? palette.solid : '#FFFFFF',
        color: active ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
        fontFamily: "'Manrope', sans-serif",
        fontSize: 13, fontWeight: 800,
        cursor: 'pointer', minHeight: 42,
        transition: 'background 140ms ease, color 140ms ease',
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: 9999,
        background: active ? '#FFFFFF' : palette.solid,
      }} />
      {label}
      <span style={{
        background: active ? 'rgba(255,255,255,0.22)' : palette.soft,
        color: active ? '#FFFFFF' : palette.text,
        padding: '2px 8px', borderRadius: 9999,
        fontSize: 11, fontWeight: 800,
      }}>
        {count}
      </span>
    </button>
  );
}
